import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { DemoProvider } from './contexts/DemoContext';
import LoginPage from './components/Auth/LoginPage';
import RoleSelection from './components/Auth/RoleSelection';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Layout from './components/Layout/Layout';
import EmployeeDashboard from './components/Dashboard/EmployeeDashboard';
import ManagerDashboard from './components/Dashboard/ManagerDashboard';
import CompanyDashboard from './components/Dashboard/CompanyDashboard';
import DeveloperDashboard from './components/Dashboard/DeveloperDashboard';
import DepartmentalGoalsDashboard from './components/Dashboard/DepartmentalGoalsDashboard';
import OrgChartImport from './components/Admin/OrgChartImport';
import CreateGoals from './components/Goals/CreateGoals';
import ViewGoal from './components/Goals/ViewGoal';
import EditGoals from './components/Goals/EditGoals';
import ReviewGoal from './components/Goals/ReviewGoal';
import EmployeeFinalReview from './components/Goals/EmployeeFinalReview';
import FinalReview from './components/Goals/FinalReview';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cps-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return (
    <DemoProvider>
    <Routes>
      <Route path="/select-role" element={<RoleSelection />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        
        {/* Employee routes - accessible by all authenticated users */}
        <Route path="dashboard" element={<EmployeeDashboard />} />
        <Route path="goals/create" element={<CreateGoals />} />
        <Route path="goals/:goalId" element={<ViewGoal />} />
        <Route path="goals/:goalId/edit" element={<EditGoals />} />
        <Route path="goals/:goalId/self-review" element={<EmployeeFinalReview />} />
        
        {/* Manager routes - requires manager, super_admin, or developer role */}
        <Route 
          path="manager" 
          element={
            <ProtectedRoute allowedRoles={['manager', 'super_admin', 'developer']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="goals/:goalId/review" 
          element={
            <ProtectedRoute allowedRoles={['manager', 'super_admin', 'developer']}>
              <ReviewGoal />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="goals/:goalId/final-review" 
          element={
            <ProtectedRoute allowedRoles={['manager', 'super_admin', 'developer']}>
              <FinalReview />
            </ProtectedRoute>
          } 
        />
        
        {/* Super Admin routes - requires super_admin or developer role */}
        <Route 
          path="company" 
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'developer']}>
              <CompanyDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="departmental" 
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'developer']}>
              <DepartmentalGoalsDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Developer routes - requires developer role only */}
        <Route 
          path="developer" 
          element={
            <ProtectedRoute allowedRoles={['developer']}>
              <DeveloperDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Admin routes - super_admin or developer */}
        <Route 
          path="admin/org-import" 
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'developer']}>
              <OrgChartImport />
            </ProtectedRoute>
          } 
        />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </DemoProvider>
  );
}

export default App;